import { useState } from 'react';
import { useNavigate } from 'react-router';
import { failureOf } from '@/api/errors';
import { useRequests } from '@/api/hooks';
import type { WireRequest } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { Button, Skeleton } from '@/ui/primitives';
import { Table, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { OpenRequest } from '@/ui/OpenRequest';
import { PageHeader } from '@/app/frame';
import { useWho } from '@/ui/who';
import { explainRequest } from './explain';

/**
 * Requests between departments (§18, amendment 0003). A department never
 * reads another's data. It asks, and the receiving department does the task
 * with what it already holds. What crosses the boundary is a record, never
 * access — and where the request went is decided at once: an agent, the
 * lead, or a refusal naming what a person would have to grant.
 */
export function whereItWent(r: WireRequest): { label: string; tone: 'warning' | 'accent' | 'neutral' | 'danger' } {
  if (r.state === 'refused') {
    return r.refusalReason?.startsWith('needs_grant:')
      ? { label: 'NEEDS A PERSON', tone: 'warning' }
      : { label: 'REFUSED', tone: 'danger' };
  }
  if (r.state === 'done') return { label: r.acknowledgedAt ? 'DONE · SEEN' : 'DONE', tone: 'accent' };
  if (r.state === 'cancelled') return { label: 'WITHDRAWN', tone: 'neutral' };
  if (r.state === 'accepted') return { label: r.assigneeAgent ? 'WITH THE AGENT' : 'WITH THE LEAD', tone: 'neutral' };
  return { label: 'ROUTING', tone: 'neutral' };
}

export function Requests() {
  const departmentLabel = useDepartmentLabel();
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const inbox = useRequests(me.department, 'inbox');
  const outbox = useRequests(me.department, 'outbox');
  const [asking, setAsking] = useState(false);
  const who = useWho();

  if (inbox.isPending || outbox.isPending) return <Skeleton rows={8} />;
  if (inbox.isError) return <div className="p-6"><Refusal failure={failureOf(inbox.error)} /></div>;
  if (outbox.isError) return <div className="p-6"><Refusal failure={failureOf(outbox.error)} /></div>;

  const rows = [
    ...inbox.data.requests.map((r) => ({ ...r, side: 'They asked' as const })),
    ...outbox.data.requests.map((r) => ({ ...r, side: 'We asked' as const })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-5 p-6 phone:p-4">
      <header className="flex flex-wrap items-center justify-end gap-3">
        <PageHeader kicker={departmentLabel(me.department)} title="Requests" />
        <Button icon="ask" variant="primary" onClick={() => setAsking(true)}>Ask another department</Button>
      </header>
      <Table<WireRequest & { side: 'They asked' | 'We asked' }>
        title="requests"
        blurb="A department never reads another's data. It asks, and the receiving department does the task with what it already holds (§18). What crosses the boundary is a record, never access."
        rows={rows}
        onOpen={(r) => void navigate(`/requests/${r.id}`)}
        explain={(r) => explainRequest(r, who, me.department)}
        testId="requests"
        emptyTitle="Nothing asked yet"
        emptyBody="Ask another department for a result, and it routes at once: an agent, the lead, or a refusal that names what a person would have to grant."
        columns={[
          { label: 'Asked for', get: (r) => r.title, primary: true },
          { label: 'From', get: (r) => departmentLabel(r.fromDepartment) },
          { label: 'To', get: (r) => departmentLabel(r.toDepartment) },
          { label: 'Opened', get: (r) => when(r.createdAt) },
          { label: 'Where it went', get: (r) => whereItWent(r).label, chip: (r) => whereItWent(r) },
        ]}
        facets={[
          { label: 'Side', get: (r) => r.side },
          { label: 'State', get: (r) => r.state[0]!.toUpperCase() + r.state.slice(1) },
        ]}
      />
      {asking && <OpenRequest onClose={() => setAsking(false)} />}
    </div>
  );
}
